import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function TermsPage() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = 'Terms of Service | Kailac';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Kailac Terms of Service — terms and conditions for using our business funding advisory services. Please read carefully before using our services.');
  }, []);

  return (
    <div className="min-h-screen bg-[#FCFAFD] pb-16 lg:pb-0">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 lg:px-8 pt-32 pb-20">
        <div className="mb-10">
          <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-3">{t('legal_eyebrow')}</p>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">{t('legal_terms_title')}</h1>
          <p className="text-sm text-gray-400">{t('legal_last_updated')}: January 1, 2024</p>
        </div>

        <div className="prose max-w-none text-gray-600 leading-relaxed space-y-8">
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">1. Acceptance of Terms</h2>
            <p>By accessing or using the services provided by Kailac (&ldquo;Company,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;), you agree to be bound by these Terms of Service. If you do not agree with these terms, do not use our services.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">2. Nature of Services</h2>
            <p className="mb-3">Kailac provides advisory and guidance services to assist qualified individuals in understanding and navigating business funding programs. <strong>Kailac is not a lender, broker, or financial institution.</strong> We do not:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Make lending decisions</li>
              <li>Guarantee funding approval</li>
              <li>Guarantee specific outcomes or results</li>
              <li>Provide regulated financial advice</li>
            </ul>
            <p className="mt-3">All funding decisions are made by independent lenders and program providers based on their own eligibility criteria.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">3. Service Fees</h2>
            <p className="mb-3">Our service fees are clearly outlined in the plan descriptions on our website:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Starter Plan:</strong> $500 upfront onboarding fee plus 4.3% success fee paid only after funding is received.</li>
              <li><strong>Full-Service Plan:</strong> 3% of funding amount paid upfront before funding is obtained.</li>
            </ul>
            <p className="mt-3">Fees are non-refundable except as explicitly stated. By selecting a plan, you agree to the applicable fee structure.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">4. No Guarantee of Results</h2>
            <p>Kailac does not guarantee any specific outcome, approval, funding amount, or timeline. Results vary based on individual financial profiles, lender requirements, and program availability. Past results described on our website are not indicative of future outcomes.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">5. Client Responsibilities</h2>
            <p>You agree to: provide accurate and complete information, respond to advisor communications promptly, and not misrepresent your financial situation. Providing false information may result in termination of services without refund.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">6. Limitation of Liability</h2>
            <p>To the maximum extent permitted by law, Kailac shall not be liable for any indirect, incidental, special, or consequential damages arising from the use of our services or inability to access funding programs.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">7. Governing Law</h2>
            <p>These Terms shall be governed by and construed in accordance with the laws of the United States. Any disputes shall be resolved through binding arbitration.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">8. Contact</h2>
            <p>Questions about these Terms: <a href="mailto:legal@kailac.com" className="text-[#4F2D63] hover:underline cursor-pointer">legal@kailac.com</a></p>
          </section>
        </div>

        <div className="mt-12 flex flex-wrap gap-4">
          <Link to="/privacy-policy" className="text-sm text-[#4F2D63] font-semibold hover:underline cursor-pointer">{t('legal_nav_privacy')}</Link>
          <Link to="/disclaimer" className="text-sm text-[#4F2D63] font-semibold hover:underline cursor-pointer">{t('legal_nav_disclaimer')}</Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
